// src/components/shell/AppShell.tsx

import { useState, useEffect } from 'react';

import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { TopBar } from './TopBar';
import { type AppId, type NavItem } from './BottomNav';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/useTheme';
import { getProjectsUrl, addNewProjectUrl } from '@/api/urls';
import type { Project } from '@/types/projects';
import type { NavItemConfig } from './NavItem';

import { Ruler, Map, FlaskConical, BrainCircuit, HardHat } from 'lucide-react';

import { DrillholesApp, type DrillholesSubNav } from '@/components/drillholes2/DrillholesApp';

const NAV_ITEMS: NavItemConfig[] = [
  {
    id: 'drillholes',
    label: 'Drillholes',
    icon: Ruler,
    subItems: [
      { id: 'holes', label: 'Holes' },
      { id: 'mapping', label: 'Mapping' },
      { id: 'analysis', label: 'Analysis' },
    ],
  },
  { id: 'surface-mapping',    label: 'Surface Mapping',    icon: Map          },
  { id: 'lab-testing',        label: 'Lab Testing',        icon: FlaskConical },
  { id: 'geotechnical-model', label: 'Geotechnical Model', icon: BrainCircuit },
  { id: 'ground-support',     label: 'Ground Support',     icon: HardHat      },
];

interface AppShellProps {
  userName: string;
  userEmail: string;
  onLogout: () => void;
}

export function AppShell({ userName, userEmail, onLogout }: AppShellProps) {
  const [activeApp, setActiveApp]     = useState<AppId>('drillholes');
  const [collapsed, setCollapsed]     = useState(false);
  const [projects, setProjects]       = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [activeSubNav, setActiveSubNav] = useState<Record<string, string>>({
    drillholes: 'data',
  });

  const { theme, toggle } = useTheme();

  useEffect(() => {
    fetch(getProjectsUrl, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch projects');
        return res.json();
      })
      .then((data: Project[]) => {
        setProjects(data);
        const lastId = localStorage.getItem('lastProjectId');
        const last = data.find((p) => p.id === lastId);
        setActiveProject(last ?? data[0] ?? null);
      })
      .catch(console.error);
  }, []);

  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSelectProject = (project: Project) => {
    setActiveProject(project);
    localStorage.setItem('lastProjectId', project.id);
  };

  const handleCreateProject = async (name: string) => {
    try {
      const res = await fetch(`${addNewProjectUrl}?project_name=${encodeURIComponent(name)}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to create project');

      const listRes = await fetch(getProjectsUrl, { credentials: 'include' });
      if (!listRes.ok) throw new Error('Failed to fetch projects');
      const data: Project[] = await listRes.json();
      setProjects(data);
      const created = data.find((p) => p.name === name);
      if (created) {
        setActiveProject(created);
        localStorage.setItem('lastProjectId', created.id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectSubNav = (appId: string, subId: string) => {
    setActiveSubNav((prev) => ({ ...prev, [appId]: subId }));
  };

  const renderApps = () => (
    <>
      <div className={activeApp === 'drillholes' ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
        <DrillholesApp subNav={activeSubNav['drillholes'] as DrillholesSubNav} />
      </div>
      {(['surface-mapping', 'lab-testing', 'geotechnical-model', 'ground-support'] as const).map((id) => (
        <div key={id} className={activeApp === id ? 'flex flex-1 items-center justify-center' : 'hidden'}>
          <span className="text-muted-foreground text-sm">
            {NAV_ITEMS.find((i) => i.id === id)?.label} coming soon
          </span>
        </div>
      ))}
    </>
  );

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <Sidebar
        items={NAV_ITEMS as NavItem[]}
        activeApp={activeApp}
        collapsed={collapsed}
        onSelect={setActiveApp}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        projects={projects}
        activeProject={activeProject}
        onSelectProject={handleSelectProject}
        onCreateProject={handleCreateProject}
        userName={userName}
        userEmail={userEmail}
        onLogout={onLogout}
        onThemeToggle={toggle}
        theme={theme}
        activeSubNav={activeSubNav}
        onSelectSubNav={handleSelectSubNav}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <TopBar
          projects={projects}
          activeProject={activeProject}
          onSelectProject={handleSelectProject}
          onCreateProject={handleCreateProject}
          userInitials={userInitials}
          onThemeToggle={toggle}
          theme={theme}
        />

        <main className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden',
          'pt-12 md:pt-0'
        )}>
          {renderApps()}
        </main>
      </div>

      <BottomNav
        items={NAV_ITEMS as NavItem[]}
        activeApp={activeApp}
        onSelect={setActiveApp}
      />
    </div>
  );
}